// client/src/coms/organisation/FilePreview.js
// Safe previews for internal library files. Images, audio, video and PDF use the browser's own players from a
// same-origin URL that the server sends with nosniff + a restrictive CSP; text is fetched as JSON and shown
// as plain text (never HTML); archives and office documents show metadata only. Nothing is executed.
import React, { useEffect, useState } from 'react';
import { apiGetJson } from './orgApi';
import { formatBytes, kindIcon } from './orgShared';

export function FilePreview({ base, file, metadata }) {
  const url = `/api/coms${base}/library/files/${file.id}/download?inline=1`;
  const mime = String(file.mime || '');
  const [text, setText] = useState(null);
  useEffect(() => {
    setText(null);
    if (!(mime.startsWith('text/') || mime === 'application/json')) return;
    apiGetJson(`${base}/library/files/${file.id}/preview-text`).then(setText, () => setText({ text: 'Preview unavailable.', truncated: false }));
  }, [base, file.id, file.updated_at, file.current_version_id, mime]);

  if (/^image\/(png|jpeg|gif|webp)$/.test(mime)) return <img className="vxo-preview-img" src={url} alt={file.name} />;
  if (mime.startsWith('audio/')) return <audio className="vxo-preview-media" src={url} controls preload="metadata" />;
  if (mime.startsWith('video/')) return <video className="vxo-preview-media" src={url} controls preload="metadata" />;
  if (mime === 'application/pdf') return <iframe className="vxo-preview-pdf" src={url} title={`Preview of ${file.name}`} />;
  if (text) {
    return (
      <div className="vxo-preview-text">
        <pre>{text.text}</pre>
        {text.truncated ? <p className="vxo-muted">Showing the first 200 KB.</p> : null}
      </div>
    );
  }
  const archive = metadata && metadata.archive;
  const doc = metadata && metadata.document;
  return (
    <div className="vxo-preview-meta">
      <div className="vxo-preview-icon" aria-hidden="true">
        {kindIcon(mime)}
      </div>
      {doc ? (
        <dl className="vxo-dl">
          {doc.title ? (
            <>
              <dt>Title</dt>
              <dd>{doc.title}</dd>
            </>
          ) : null}
          {doc.creator ? (
            <>
              <dt>Author</dt>
              <dd>{doc.creator}</dd>
            </>
          ) : null}
          {doc.modified ? (
            <>
              <dt>Modified</dt>
              <dd>{doc.modified}</dd>
            </>
          ) : null}
          {doc.pages ? (
            <>
              <dt>Pages</dt>
              <dd>{doc.pages}</dd>
            </>
          ) : null}
        </dl>
      ) : null}
      {archive ? (
        <div className="vxo-archive">
          <p>
            {archive.count} entries · {formatBytes(archive.total_uncompressed)} uncompressed{archive.suspicious ? ' · unusually high compression' : ''}
          </p>
          <ul className="vxo-plain vxo-archive-list">
            {archive.entries.map((e, i) => (
              <li key={`${e.name}-${i}`}>
                {e.name} <span className="vxo-muted">{formatBytes(e.size)}</span>
              </li>
            ))}
          </ul>
          {archive.truncated ? <p className="vxo-muted">Only the first entries are listed.</p> : null}
        </div>
      ) : null}
      {!doc && !archive ? <p className="vxo-muted">No preview for this file type. Download it to open it.</p> : null}
    </div>
  );
}
